import {
  IEntryRecordOrderBy,
  ITypedEntityOrderBy,
  IModificationRecordOrderBy,
  IGenericEntityOrderBy,
  IOwnedOrderBy,
  IVersionedOrderBy,
  ICreatedRecordParameters as IEntryRecordParameters,
  ITypedEntityParameters,
  IGenericRequestParameters,
  IModifiedRecordParameters,
  IOwnedEntityParameters,
  IPaginationParameters,
  ISoftDeletableFilterParameters,
  IVersionedEntityParameters,
  IdTypes,
  RequireKeys,
  StringIndexedDict,
  IHierarchyParameters,
  INamedEntityParameters,
  IProjectBasedParameters,
  IListRequestParameters,
  IModificationRecord,
  IEntryRecord,
  ICustomTypedEntity,
  IPermissionedEntity,
  INamedEntity,
  IUniqueEntity,
  IRelatedEntity,
  ISoftDeletable,
  IHierarchyRead,
  IConcurrencyToken,
  IUniqueEntityParameters,
  ISessionedEntity,
  ILockableEntity,
  ICustomFieldSearch,
  ILockableEntityParameters,
  ISignableEntity,
  ISignableEntityParameters,
  INamedEntityOrderBy,
} from "./GenericTypes";

export type PropertyTranslatorArr<T extends string> = { [K in T]: string };
export type PropertyTranslator<T extends StringIndexedDict> = { [key in keyof RequireKeys<T, keyof T>]: string }; // Make all properties (keys) required with a string value
export type FiltersTranslator<Filters extends IListRequestParameters<Filters["sortBy"]>> = PropertyTranslator<Filters>;

// Order By translators
export const DefaultOrderTypeTranslator: PropertyTranslatorArr<IGenericEntityOrderBy> = {
  ID: "ID",
} as const;

export const DefaultNamedOrderTypeTranslator: PropertyTranslatorArr<INamedEntityOrderBy> = {
  NAME: "Name",
} as const;

export const DefaultOwnerOrderTypeTranslator: PropertyTranslatorArr<IOwnedOrderBy> = {
  OWNER: "Owner",
} as const;

export const DefaultCustomSchemaOrderTypeTranslator: PropertyTranslatorArr<ITypedEntityOrderBy> = {
  CUSTOM_FIELD: "Custom field",
  CUSTOM_TYPE: "Custom type",
} as const;

export const DefaultEnteredOnRecordOrderTypeTranslator: PropertyTranslatorArr<IEntryRecordOrderBy> = {
  ENTERED_ON: "Entered on",
  ENTERED_BY: "Entered by",
} as const;

export const DefaultModifiedOnRecordOrderTypeTranslator: PropertyTranslatorArr<IModificationRecordOrderBy> = {
  MODIFIED_ON: "Last modified on",
  MODIFIED_BY: "Last modified by",
} as const;

export const DefaultVersionedOrderTypeTranslator: PropertyTranslatorArr<IVersionedOrderBy> = {
  VERSION: "Version",
} as const;

// Filter translators
export const INamedEntityParametersTranslator: PropertyTranslator<INamedEntityParameters> = {
  names: "Names",
} as const;

export const IPaginationParametersTranslator: PropertyTranslator<IPaginationParameters> = {
  page: "Page",
  pageSize: "Page size",
} as const;

export const IOwnedEntityParametersTranslator: PropertyTranslator<IOwnedEntityParameters> = {
  ownerIds: "Owner IDs",
} as const;

export const IProjectBasedParametersTranslator: PropertyTranslator<IProjectBasedParameters> = {
  projectIds: "Project IDs",
} as const;

export const IGenericRequestParametersTranslator: PropertyTranslator<IGenericRequestParameters<{ id: IdTypes }>> = {
  sortBy: "Sort by",
  excludeIds: "Exclude IDs",
  ids: "IDs",
  includeCount: "Include entity count",
  name: "Name",
  searchTerm: "Search term",
  searchTermIncludeIds: "IDs",
  searchTermIncludeNames: "names",
  searchTermIncludeUuids: "UUIDs",
  searchTermIncludeCustomFieldValues: "Content",
} as const;

export const IEntryRecordParametersTranslator: PropertyTranslator<IEntryRecordParameters> = {
  enteredByIds: "Entered by IDs",
  enteredFrom: "Entered from",
  enteredTo: "Entered to",
} as const;

export const IModifiedRecordParametersTranslator: PropertyTranslator<IModifiedRecordParameters> = {
  modifiedByIds: "Last modified by IDs",
  modifiedFrom: "Last modified from",
  modifiedTo: "Last modified to",
} as const;

export const IVersionedEntityParametersTranslator: PropertyTranslator<IVersionedEntityParameters> = {
  original_ids: "Original IDs",
  verions: "Versions",
} as const;

export const ISoftDeletableFilterParametersTranslator: PropertyTranslator<ISoftDeletableFilterParameters> = {
  includeSoftDeleted: "Include trashed",
  isSoftDeleted: "Is trashed",
} as const;

export const ICustomFieldSearchTranslator: PropertyTranslator<ICustomFieldSearch> = {
  filterQuery: "Custom field filter query",
} as const;

export const ITypedEntityParametersTranslator: PropertyTranslator<ITypedEntityParameters> = {
  ...ICustomFieldSearchTranslator,
  customTypeIds: "Custom type ID(s)",
  customFieldIds: "Custom field ID(s)",
} as const;

export const IHierarchyParametersTranslator: PropertyTranslator<IHierarchyParameters> = {
  childrenOfParentIds: "Children of parent ID(s)",
  descendantsOfIds: "Descendants of ID(s)",
  excludeHierarchyChildren: "Exclude hierarchy children",
  isHierarchyRoot: "Is part of hierarchy",
  inventoryIds: "Inventory IDs",
} as const;

export const ILockableEntityParametersTranslator: PropertyTranslator<ILockableEntityParameters> = {
  lockedByIds: "Locked by IDs",
  lockedFrom: "Locked from",
  lockedTo: "Locked to",
  isLocked: "Is locked",
};

export const ISignableEntityParametersTranslator: PropertyTranslator<ISignableEntityParameters> = {
  signedByIds: "Signed by IDs",
  notSignedByIds: "Not signed by IDs",
  signedFrom: "Signed from",
  signedTo: "Signed to",
  signatureTypes: "Signature status",
  isSigned: "Is signed",
};

// Field label translators
export const INamedEntityLabels: PropertyTranslator<INamedEntity> = {
  name: "Name",
} as const;

export const IUniqueEntityLabels: PropertyTranslator<IUniqueEntity> = {
  origin: "Origin",
  uid: "UUID",
  importExportEvents: "Import/export events",
} as const;

export const IPermissionedEntityLabels: PropertyTranslator<IPermissionedEntity> = {
  permissions: "Permissions",
} as const;

export const ISoftDeletableLabels: PropertyTranslator<ISoftDeletable> = {
  isDeleted: "Is trashed",
} as const;

export const IRelatedEntityLabels: PropertyTranslator<IRelatedEntity<any>> = {
  relations: "Relations",
} as const;

export const ICustomTypedEntityLabels: PropertyTranslator<ICustomTypedEntity> = {
  customType: "Custom type",
  customFields: "Custom fields",
  customValues: "Custom field values",
} as const;

export const IEntryRecordLabels: PropertyTranslator<IEntryRecord> = {
  enteredBy: "Entered by",
  enteredOn: "Entered on",
} as const;

export const IModificationRecordLabels: PropertyTranslator<IModificationRecord> = {
  modifiedBy: "Last modified by",
  modifiedOn: "Last modified on",
} as const;

export const IHierarchyReadLabels: PropertyTranslator<IHierarchyRead> = {
  ancestors: "Path",
  parent: "Parent",
} as const;

export const IConcurrencyTokenLabels: PropertyTranslator<IConcurrencyToken<string>> = {
  concurrencyToken: "Concurrency token",
};

export const IUniqueEntityParametersTranslator: PropertyTranslator<IUniqueEntityParameters> = {
  originIds: "Origin IDs",
} as const;

export const ISessionedEntityLabels: PropertyTranslator<ISessionedEntity> = {
  currentlyEditedBy: "Locked by",
  currentlyEditedSince: "Locked on",
  isCurrentlyEdited: "Is locked",
  modifiedOn: IModificationRecordLabels.modifiedOn,
  publicSessionId: "Public session ID",
} as const;

export const ILockableEntityLabels: PropertyTranslator<ILockableEntity> = {
  lockedBy: "Locked by",
  lockedOn: "Locked on",
  isLocked: "Is locked",
} as const;

export const ISignableEntityLabels: PropertyTranslator<ISignableEntity> = {
  isSigned: "Is signed",
  signatures: "Signatures",
} as const;
